import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';

type Locale = 'hr' | 'en';

/**
 * Pinokio FAB - globalni floating action button za Tasks2 modul.
 *
 * Donji desni kut, lijevo od AiChatBubble (right: 88px, bottom: 20px).
 * Prikazuje badge s brojem neprocitanih Tasks2 notifikacija.
 * Klik otvara Tasks2 modul (fullscreen overlay z-index 4000).
 *
 * Poll: svake 30s GET /tasks2/notifications -> unread_count.
 * Audio ping kad se unread_count poveca.
 */

type NotifCountResp = { unread_count: number };

type MyCardsResp = {
    overdue: unknown[];
    today: unknown[];
    upcoming: unknown[];
    later: unknown[];
    no_date: unknown[];
};

export function PinokioFab({
    token,
    locale,
    onOpen,
}: {
    token: string | null;
    locale: Locale;
    onOpen: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [unread, setUnread] = useState(0);
    const [myCount, setMyCount] = useState(0);
    const [hover, setHover] = useState(false);
    const [pulse, setPulse] = useState(false);
    const lastUnread = useRef(-1);

    const pollNotif = useCallback(async () => {
        if (!token) return;
        try {
            const r = await api<NotifCountResp>('/tasks2/notifications?limit=1', token);
            const count = r.unread_count ?? 0;
            if (lastUnread.current >= 0 && count > lastUnread.current) {
                playPing();
                setPulse(true);
                setTimeout(() => setPulse(false), 1200);
            }
            lastUnread.current = count;
            setUnread(count);
        } catch { /* silent */ }
    }, [token]);

    const pollCards = useCallback(async () => {
        if (!token) return;
        try {
            const r = await api<MyCardsResp>('/tasks2/my-cards', token);
            const total =
                (r.overdue?.length ?? 0) +
                (r.today?.length ?? 0) +
                (r.upcoming?.length ?? 0) +
                (r.later?.length ?? 0) +
                (r.no_date?.length ?? 0);
            setMyCount(total);
        } catch { /* silent */ }
    }, [token]);

    useEffect(() => {
        void pollNotif();
        void pollCards();
        const a = window.setInterval(() => { void pollNotif(); }, 30_000);
        const b = window.setInterval(() => { void pollCards(); }, 60_000);
        return () => { window.clearInterval(a); window.clearInterval(b); };
    }, [pollNotif, pollCards]);

    const hasOverdue = false; // TODO: faza 4+ - poll overdue count

    // Tooltip tekst
    const tip = unread > 0
        ? `${unread} ${t('neprocitanih', 'unread')} - ${myCount} ${t('kartica', 'cards')}`
        : `${myCount} ${t('mojih kartica', 'my cards')}`;

    return (
        <button
            type="button"
            onClick={onOpen}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            title={tip}
            style={{
                position: 'fixed',
                right: 88,
                bottom: 20,
                zIndex: 1200,
                width: 54,
                height: 54,
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                background: unread > 0
                    ? 'linear-gradient(135deg, #c99b31, #a67c00)'
                    : 'linear-gradient(135deg, #6b4f1c, #3d2e0f)',
                color: '#fff',
                boxShadow: hover
                    ? '0 6px 24px rgba(107,79,28,0.5)'
                    : '0 4px 16px rgba(107,79,28,0.35)',
                transform: hover ? 'scale(1.08)' : pulse ? 'scale(1.12)' : 'scale(1)',
                transition: 'transform 0.2s, box-shadow 0.2s, background 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Arial, sans-serif',
            }}
        >
            {/* Pinokio ikona - kanban board simbol */}
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="18" rx="1.5" />
                <rect x="14" y="3" width="7" height="12" rx="1.5" />
            </svg>

            {/* Badge - neprocitane notifikacije */}
            {unread > 0 ? (
                <span style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    minWidth: 20,
                    height: 20,
                    borderRadius: 10,
                    background: '#b14040',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 5px',
                    boxShadow: '0 2px 6px rgba(177,64,64,0.5)',
                    animation: pulse ? 'pinokio-pulse 0.6s ease-in-out 2' : 'none',
                }}>
                    {unread > 99 ? '99+' : unread}
                </span>
            ) : null}

            {/* Sekundarni badge - broj kartica (mali, donji lijevi) */}
            {myCount > 0 && unread === 0 ? (
                <span style={{
                    position: 'absolute',
                    bottom: -2,
                    left: -2,
                    minWidth: 18,
                    height: 18,
                    borderRadius: 9,
                    background: '#1e40af',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                    boxShadow: '0 2px 6px rgba(30,64,175,0.4)',
                }}>
                    {myCount > 99 ? '99+' : myCount}
                </span>
            ) : null}

            {/* Pulse animacija CSS - injektira jednom */}
            <PulseStyle />
        </button>
    );
}

/** Injektira @keyframes pinokio-pulse jednom u <head>. */
let _styleInjected = false;
function PulseStyle() {
    useEffect(() => {
        if (_styleInjected) return;
        _styleInjected = true;
        const style = document.createElement('style');
        style.textContent = `
            @keyframes pinokio-pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.3); }
            }
        `;
        document.head.appendChild(style);
    }, []);
    return null;
}

/** Audio ping - kratki ding za novu Tasks2 notifikaciju. */
function playPing(): void {
    try {
        const Ctx = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
            ?? (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(660, ctx.currentTime);     // E5 - razlicit od Tasks v1
        osc.frequency.setValueAtTime(990, ctx.currentTime + 0.06); // B5
        osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.12); // E6
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
        osc.start(); osc.stop(ctx.currentTime + 0.28);
        osc.onended = () => { try { ctx.close(); } catch { /* */ } };
    } catch { /* silent */ }
}
